export const ORIGIN_NONE = 'none';
export const ORIGIN_TOP_LEFT = 'topLeft';
export const ORIGIN_TOP = 'top';
export const ORIGIN_TOP_RIGHT = 'topRight';
export const ORIGIN_LEFT = 'left';
export const ORIGIN_CENTER = 'center';
export const ORIGIN_RIGHT = 'right';
export const ORIGIN_BOTTOM_LEFT = 'bottomLeft';
export const ORIGIN_BOTTOM = 'bottom';
export const ORIGIN_BOTTOM_RIGHT = 'bottomRight';

/** automatic origin for vertical alignment. eg: dropdowns. */
export const ORIGIN_AUTO_VERTICAL = 'autoVertical';

/** automatic origin to align to element's corner of the quadrant it's in */
export const ORIGIN_AUTO_GRID_INNER = 'autoGridInner';

export const SIZE_NONE = 'sizeNone';
export const SIZE_FULL = 'sizeFull';
export const SIZE_AUTO = 'sizeAuto';
export const SIZE_COVER = 'sizeCover';

export const POPOVER_MIN_WIDTH = 200;
export const POPOVER_MIN_HEIGHT = 100;
export const POPOVER_WINDOW_MARGIN = 5;

const PAGE_TOP = 'pageTop';
const PAGE_MIDDLE = 'pageMiddle';
const PAGE_LEFT = 'pageLeft';
const PAGE_RIGHT = 'pageRight';

export function autoWidth(
    targetPosition,
    { scrollLeft = 0, innerWidth = 0, threshold = null, margin = POPOVER_WINDOW_MARGIN } = {},
) {
    let targetWidth;
    if (targetPosition.left != null) {
        targetWidth = innerWidth + scrollLeft - targetPosition.left;
    } else if (targetPosition.right != null) {
        targetWidth = targetPosition.right - scrollLeft;
    }

    if (threshold != null && targetWidth >= threshold) {
        // If it's beyond the threshold, don't specify the dimension
        return null;
    } else if (margin != null) {
        // Shave off the margins
        return targetWidth - margin;
    }
    return targetWidth;
}

export function autoHeight(
    targetPosition,
    { scrollTop = 0, innerHeight = 0, threshold = null, margin = POPOVER_WINDOW_MARGIN } = {},
) {
    let targetHeight = null;
    if (targetPosition.top != null) {
        targetHeight = innerHeight + scrollTop - targetPosition.top;
    } else if (targetPosition.bottom != null) {
        targetHeight = targetPosition.bottom - scrollTop;
    }

    if (threshold != null && targetHeight >= threshold) {
        // If it's beyond the threshold, don't specify the dimension
        return null;
    } else if (margin != null) {
        // Shave off the margins
        return targetHeight - margin;
    }
    return targetHeight;
}

export const constrainedTargetWidth =
    ({ min = POPOVER_MIN_WIDTH, max = null } = {}) =>
    (targetPosition, options) => {
        const targetWidth = autoWidth(targetPosition, options);
        if (targetWidth == null) {
            return targetWidth;
        } else if (max != null && targetWidth > max) {
            return max;
        } else if (min != null && targetWidth < min) {
            return min;
        }
        return targetWidth;
    };

export const constrainedTargetHeight =
    ({ min = POPOVER_MIN_HEIGHT, max = null } = {}) =>
    (targetPosition, options) => {
        const targetHeight = autoHeight(targetPosition, options);
        if (targetHeight == null) {
            return targetHeight;
        } else if (max != null && targetHeight > max) {
            return max;
        } else if (min != null && targetHeight < min) {
            return min;
        }
        return targetHeight;
    };

export const constainedAutoWidth = constrainedTargetWidth();
export const constainedAutoHeight = constrainedTargetHeight();

/**
 * Returns the page section that the target should render based on the anchor and target positions
 * and dimensions. Page is divided in "pageMiddle", "pageBottom", "pageLeft" and "pageRight"
 * +==================+
 * |###            ###|
 * |###            ###|
 * |###            ###|
 * |###************###|
 * |###************###|
 * +==================+
 *
 * @param {object} target
 * @param {object} anchor
 * @param {object} windowObject
 */
function getGridInnerPageArea(target, anchor, windowObject) {
    const { innerWidth } = windowObject;
    const targetWidth = target.right - target.left;
    const anchorWidth = anchor.right - anchor.left;
    const anchorHeight = anchor.bottom - anchor.top;
    const anchorMiddleX = anchor.left + anchorWidth / 2;
    const anchorMiddleY = anchor.top + anchorHeight / 2;

    if (anchorMiddleX - targetWidth < 0) {
        return PAGE_LEFT;
    }
    if (anchorMiddleX + targetWidth > innerWidth) {
        return PAGE_RIGHT;
    }
    // if on the top part of the screen, we display it to the bottom
    if (anchorMiddleY < innerWidth * 0.25) {
        return PAGE_TOP;
    }
    return PAGE_MIDDLE;
}

/**
 * For the grid inner mode, used by SuiTip, returns the origin the anchor should use depending
 * on the area that the anchor/target are in.
 * @param {string} pageArea
 */
function getGridInnerAnchorOrigin(pageArea) {
    switch (pageArea) {
        case PAGE_TOP:
            return ORIGIN_BOTTOM;
        case PAGE_LEFT:
            return ORIGIN_RIGHT;
        case PAGE_RIGHT:
            return ORIGIN_LEFT;
        default:
            return ORIGIN_TOP;
    }
}

/**
 * For the grid inner mode, used by SuiTip, returns the origin the target should use depending
 * on the area that the anchor/target are in.
 * @param {string} pageArea
 */
function getGridInnerTargetOrigin(pageArea) {
    switch (pageArea) {
        case PAGE_TOP:
            return ORIGIN_TOP;
        case PAGE_LEFT:
            return ORIGIN_LEFT;
        case PAGE_RIGHT:
            return ORIGIN_RIGHT;
        default:
            return ORIGIN_BOTTOM;
    }
}

/**
 * @param {object} offset
 * @return {number} Middle point between left and right.
 */
function getMiddleX(offset) {
    return (offset.right + offset.left) / 2;
}
/**
 * @param {object} offset
 * @return {number} Middle point between top and bottom.
 */
function getMiddleY(offset) {
    return (offset.bottom + offset.top) / 2;
}

/**
 * Calculates the position for an element using the relative offset between a reference point on
 * the element and a reference point on an anchor element
 *
 * @param  {Object} target - relative offsets of the target element to move
 * @param  {Object} anchor - absolute positioning of the anchor element
 * @param  {Object|Function} targetOrigin - determines the reference point of the target element
 * @param  {Object|Function} anchorOrigin - determines the reference point of the anchor element
 * @param  {Object|Function} targetWidth - determines the width of the target element
 * @param  {Object|Function} targetHeight - determines the height of the target element
 * @param  {Object} window - to mock dimensions by tests.
 * @return {Object} target position
 */
export const positionTargetToAnchor = (
    target,
    anchor,
    targetOrigin,
    anchorOrigin,
    targetWidth,
    targetHeight,
    windowObject = window,
) => {
    if (targetOrigin === ORIGIN_NONE || anchorOrigin === ORIGIN_NONE) {
        return {};
    }

    const targetPosition = {};
    const { scrollTop, scrollLeft } = document.scrollingElement || document.body;
    const { innerWidth, innerHeight } = windowObject;

    // Make some reasonable assumptions when trying to auto-position
    /* eslint-disable no-param-reassign */
    if (anchorOrigin === ORIGIN_AUTO_VERTICAL && targetOrigin === ORIGIN_AUTO_VERTICAL) {
        const preferAlignToTop =
            // Opening upward would go offscreen
            anchor.bottom - target.bottom < scrollTop + POPOVER_WINDOW_MARGIN ||
            // Opening downward would not go offscreen
            anchor.top + target.bottom < scrollTop + (innerHeight - POPOVER_WINDOW_MARGIN);
        if (preferAlignToTop) {
            targetOrigin = ORIGIN_TOP_LEFT;
            anchorOrigin = ORIGIN_TOP_LEFT;
        } else {
            targetOrigin = ORIGIN_BOTTOM_LEFT;
            anchorOrigin = ORIGIN_BOTTOM_LEFT;
        }
    } else if (targetOrigin === ORIGIN_AUTO_VERTICAL) {
        targetOrigin = ORIGIN_TOP_LEFT;
    } else if (anchorOrigin === ORIGIN_AUTO_VERTICAL) {
        anchorOrigin = ORIGIN_TOP_LEFT;
    }

    if (anchorOrigin === ORIGIN_AUTO_GRID_INNER || targetOrigin === ORIGIN_AUTO_GRID_INNER) {
        const pageArea = getGridInnerPageArea(target, anchor, windowObject);
        if (anchorOrigin === ORIGIN_AUTO_GRID_INNER) {
            anchorOrigin = getGridInnerAnchorOrigin(pageArea);
        }
        if (targetOrigin === ORIGIN_AUTO_GRID_INNER) {
            targetOrigin = getGridInnerTargetOrigin(pageArea);
        }
    }

    /* eslint-enable no-param-reassign */

    // Start from the anchor position
    if (typeof anchorOrigin === 'function') {
        const anchorOriginPosition = anchorOrigin(anchor);
        if (anchorOriginPosition.top != null) {
            targetPosition.top = anchorOriginPosition.top;
        } else if (anchorOriginPosition.bottom != null) {
            targetPosition.bottom = anchorOriginPosition.bottom;
        } else {
            targetPosition.top = 0;
        }
        if (anchorOriginPosition.left != null) {
            targetPosition.left = anchorOriginPosition.left;
        } else if (anchorOriginPosition.right != null) {
            targetPosition.right = anchorOriginPosition.right;
        } else {
            targetPosition.right = 0;
        }
    } else {
        // Vertical
        switch (anchorOrigin) {
            case ORIGIN_TOP_LEFT:
            case ORIGIN_TOP:
            case ORIGIN_TOP_RIGHT:
                targetPosition.top = anchor.top;
                break;
            case ORIGIN_LEFT:
            case ORIGIN_CENTER:
            case ORIGIN_RIGHT:
                targetPosition.top = getMiddleY(anchor);
                break;
            case ORIGIN_BOTTOM_LEFT:
            case ORIGIN_BOTTOM:
            case ORIGIN_BOTTOM_RIGHT:
                targetPosition.top = anchor.bottom;
                break;
            default:
                targetPosition.top = 0;
                break;
        }
        // Horizontal
        switch (anchorOrigin) {
            case ORIGIN_TOP_LEFT:
            case ORIGIN_LEFT:
            case ORIGIN_BOTTOM_LEFT:
                targetPosition.left = anchor.left;
                break;
            case ORIGIN_TOP:
            case ORIGIN_CENTER:
            case ORIGIN_BOTTOM:
                targetPosition.left = getMiddleX(anchor);
                break;
            case ORIGIN_TOP_RIGHT:
            case ORIGIN_RIGHT:
            case ORIGIN_BOTTOM_RIGHT:
                targetPosition.left = anchor.right;
                break;
            default:
                targetPosition.left = 0;
                break;
        }
    }

    // Offset based on the target origin
    if (typeof targetOrigin === 'function') {
        const targetOriginOffset = targetOrigin(target);
        if (targetPosition.top != null) {
            targetPosition.top += targetOriginOffset.yOffset;
        } else if (targetPosition.bottom != null) {
            targetPosition.bottom -= targetOriginOffset.yOffset;
        }
        if (targetPosition.left != null) {
            targetPosition.left += targetOriginOffset.xOffset;
        } else if (targetPosition.right != null) {
            targetPosition.right -= targetOriginOffset.xOffset;
        }
    } else {
        // Vertical
        switch (targetOrigin) {
            case ORIGIN_TOP_LEFT:
            case ORIGIN_TOP:
            case ORIGIN_TOP_RIGHT:
                if (targetPosition.top != null) {
                    targetPosition.top -= target.top;
                } else if (targetPosition.bottom != null) {
                    targetPosition.bottom += target.top;
                }
                break;
            case ORIGIN_LEFT:
            case ORIGIN_CENTER:
            case ORIGIN_RIGHT:
                if (targetPosition.top != null) {
                    targetPosition.top -= (target.bottom - target.top) / 2;
                } else if (targetPosition.bottom != null) {
                    targetPosition.bottom += (target.bottom - target.top) / 2;
                }
                break;
            case ORIGIN_BOTTOM_LEFT:
            case ORIGIN_BOTTOM:
            case ORIGIN_BOTTOM_RIGHT:
                if (targetPosition.top != null) {
                    targetPosition.top -= target.bottom;
                } else if (targetPosition.bottom != null) {
                    targetPosition.bottom += target.bottom;
                }
                break;
            default:
                break;
        }
        // Horizontal
        switch (targetOrigin) {
            case ORIGIN_TOP_LEFT:
            case ORIGIN_LEFT:
            case ORIGIN_BOTTOM_LEFT:
                if (targetPosition.left != null) {
                    targetPosition.left -= target.left;
                } else if (targetPosition.right != null) {
                    targetPosition.right += target.left;
                }
                break;
            case ORIGIN_TOP:
            case ORIGIN_CENTER:
            case ORIGIN_BOTTOM:
                if (targetPosition.left != null) {
                    targetPosition.left -= (target.right - target.left) / 2;
                } else if (targetPosition.right != null) {
                    targetPosition.right += (target.right - target.left) / 2;
                }
                break;
            case ORIGIN_TOP_RIGHT:
            case ORIGIN_RIGHT:
            case ORIGIN_BOTTOM_RIGHT:
                if (targetPosition.left != null) {
                    targetPosition.left -= target.right;
                } else if (targetPosition.right != null) {
                    targetPosition.right += target.right;
                }
                break;
            default:
                break;
        }
    }

    // Determine width
    if (typeof targetWidth === 'function') {
        targetPosition.width = targetWidth(targetPosition, {
            scrollLeft,
            scrollTop,
            innerWidth,
            innerHeight,
            threshold: target.right,
            margin: POPOVER_WINDOW_MARGIN,
        });
    } else {
        switch (targetWidth) {
            case SIZE_NONE:
                break;
            case SIZE_FULL:
                targetPosition.left = anchor.left;
                targetPosition.width = anchor.width;
                break;
            case SIZE_AUTO:
                targetPosition.width = constainedAutoWidth(targetPosition, {
                    scrollLeft,
                    scrollTop,
                    innerWidth,
                    innerHeight,
                    threshold: target.right,
                    margin: POPOVER_WINDOW_MARGIN,
                });
                break;
            case SIZE_COVER:
                targetPosition.right = 0;
                targetPosition.left = 0;
                targetPosition.width = '100%';
                break;
            default:
                break;
        }
    }

    // Determine height
    if (typeof targetHeight === 'function') {
        targetPosition.height = targetHeight(targetPosition, {
            scrollLeft,
            scrollTop,
            innerWidth,
            innerHeight,
            threshold: target.bottom,
            margin: POPOVER_WINDOW_MARGIN,
        });
    } else {
        switch (targetHeight) {
            case SIZE_NONE:
                break;
            case SIZE_FULL:
                targetPosition.top = anchor.top;
                targetPosition.height = anchor.height;
                break;
            case SIZE_AUTO:
                targetPosition.height = constainedAutoHeight(targetPosition, {
                    scrollLeft,
                    scrollTop,
                    innerWidth,
                    innerHeight,
                    threshold: target.bottom,
                    margin: POPOVER_WINDOW_MARGIN,
                });
                break;
            case SIZE_COVER:
                targetPosition.top = 0;
                targetPosition.bottom = 0;
                targetPosition.height = '100%';
                break;
            default:
                break;
        }
    }

    // Add units
    Object.keys(targetPosition).forEach((key) => {
        if (typeof targetPosition[key] === 'number') {
            targetPosition[key] += 'px';
        }
    });

    return targetPosition;
};
